import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Spark-store. Per product/design/spark-feed-freemium-research.md §1+§2:
 *
 *   - Cap free Spark at 20 cards/calendar-day.
 *   - Track which deck cards have been viewed so re-mounting the feed
 *     doesn't double-count, and so the "Up next" cursor survives reloads.
 *   - Reset the day counter at local midnight on first interaction the
 *     next day.
 *
 * Why per-domain (not on `progress` or `signals`)? Spark caps are a
 * surface concern — they don't belong in mastery state; the cap is also
 * the lever the freemium paywall (build queue step 11) hooks into.
 */

export const DAILY_CARD_CAP = 20;

interface SparkState {
  /** Local calendar day key (YYYY-MM-DD) for the current count window. */
  dayKey: string | null;
  cardsViewedToday: number;
  /** Card ids the student has actually viewed at least once (any day). */
  seenCardIds: string[];
  /** When the student dismissed the one-time swipe-up tutorial. */
  tutorialDismissedAt: string | null;
  /** When the student dismissed the mid-feed "take the check-in?" prompt. */
  softPromptDismissedAt: string | null;

  recordCardView: (cardId: string, nowMs?: number) => void;
  dismissTutorial: () => void;
  dismissSoftPrompt: () => void;
  reset: () => void;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const SEEN_CAP = 200; // trailing window — we never need more than this for v0

function dayKeyOf(nowMs: number): string {
  const d = new Date(nowMs);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const useSpark = create<SparkState>()(
  persist(
    (set) => ({
      dayKey: null,
      cardsViewedToday: 0,
      seenCardIds: [],
      tutorialDismissedAt: null,
      softPromptDismissedAt: null,

      recordCardView: (cardId, nowMs = Date.now()) =>
        set((s) => {
          const today = dayKeyOf(nowMs);
          const sameDay = s.dayKey === today;
          const alreadyCountedToday = sameDay && s.seenCardIds.includes(cardId);
          // We bump the daily counter on first view *within this day*, even
          // if the card was viewed on a previous day. The 20-card cap is a
          // surface budget, not a uniqueness budget.
          const nextCount = sameDay
            ? alreadyCountedToday
              ? s.cardsViewedToday
              : s.cardsViewedToday + 1
            : 1;
          const nextSeen = s.seenCardIds.includes(cardId)
            ? s.seenCardIds
            : [...s.seenCardIds.slice(-SEEN_CAP + 1), cardId];
          return {
            dayKey: today,
            cardsViewedToday: nextCount,
            seenCardIds: nextSeen,
          };
        }),
      dismissTutorial: () => set({ tutorialDismissedAt: new Date().toISOString() }),
      dismissSoftPrompt: () => set({ softPromptDismissedAt: new Date().toISOString() }),
      reset: () =>
        set({
          dayKey: null,
          cardsViewedToday: 0,
          seenCardIds: [],
          tutorialDismissedAt: null,
          softPromptDismissedAt: null,
        }),
    }),
    { name: 'disha.spark', version: 1 },
  ),
);

/**
 * Selector: how many cards remain in today's free allowance. Re-evaluates
 * the day key against `nowMs` so a stale "yesterday count" doesn't gate
 * today's feed.
 */
export function cardsRemainingToday(state: SparkState, nowMs: number = Date.now()): number {
  const today = dayKeyOf(nowMs);
  if (state.dayKey !== today) return DAILY_CARD_CAP;
  return Math.max(0, DAILY_CARD_CAP - state.cardsViewedToday);
}

/** Convenience hook variant. */
export function useCardsRemainingToday(): number {
  const dayKey = useSpark((s) => s.dayKey);
  const count = useSpark((s) => s.cardsViewedToday);
  const today = dayKeyOf(Date.now());
  if (dayKey !== today) return DAILY_CARD_CAP;
  return Math.max(0, DAILY_CARD_CAP - count);
}

/** Hours until local midnight — for the "come back tomorrow" framing. */
export function hoursUntilMidnight(nowMs: number = Date.now()): number {
  const d = new Date(nowMs);
  d.setHours(24, 0, 0, 0);
  return Math.max(0, Math.round((d.getTime() - nowMs) / (60 * 60 * 1000)));
}

export { dayKeyOf, MS_PER_DAY };
